import { useMemo, Fragment, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart, Legend,
  ComposedChart, ScatterChart, Scatter, ReferenceLine, ZAxis,
} from "recharts";
import { Loader2, TrendingUp, AlertCircle, BarChart3, Activity, Globe } from "lucide-react";

const COLORS = { win: "#00E676", loss: "#FF3D00", neutral: "#007AFF", amber: "#FFC107", purple: "#9C27B0", kde: "#FFD600", gauss: "#FF6D00" };
const STATUS_COLORS = { good: "#00E676", warn: "#FFC107", bad: "#FF3D00" };

function ChartBox({ children, className = "h-56" }) {
  return (
    <div className={`w-full min-w-0 ${className}`} style={{ minHeight: 180 }}>
      <ResponsiveContainer width="100%" height="100%" minWidth={0}>
        {children}
      </ResponsiveContainer>
    </div>
  );
}

function ChartTooltip({ active, payload, label, prefix = "$", suffix = "" }) {
  if (!active || !payload?.length) return null;
  const val = payload[0].value;
  const formatted = prefix === "$"
    ? `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
    : `${Number(val).toLocaleString(undefined, { maximumFractionDigits: 3 })}${suffix}`;
  return (
    <div className="bg-[#111111] border border-[#27272A] rounded-lg px-3 py-2 text-xs">
      {label != null && <p className="text-[#A1A1AA] mb-1">{label}</p>}
      <p className="font-data font-bold text-white">{formatted}</p>
    </div>
  );
}

function EmptySection({ message }) {
  return (
    <div className="text-center py-12 text-[#52525B] text-sm">
      {message || "Re-sync your MT5 journal to generate this section."}
    </div>
  );
}

function mergeDistributionData(dist) {
  if (!dist?.histogram?.length) return [];
  const gauss = dist.gaussian_curve || [];
  const kde = dist.kde_curve || [];
  const nearest = (arr, x) => {
    if (!arr.length) return 0;
    let best = arr[0];
    let bestD = Math.abs(arr[0].x - x);
    for (const p of arr) {
      const d = Math.abs(p.x - x);
      if (d < bestD) { best = p; bestD = d; }
    }
    return best.y;
  };
  return dist.histogram.map((h) => ({
    mid: h.mid,
    count: h.count,
    side: h.side,
    gaussian: nearest(gauss, h.mid),
    kde: nearest(kde, h.mid),
  }));
}

function CorrelationHeatmap({ correlation }) {
  const { symbols = [], matrix = [] } = correlation || {};
  if (symbols.length < 2) {
    return <EmptySection message="Need at least 2 instruments with enough trades for correlation." />;
  }
  const cellColor = (v) => {
    if (v >= 0.7) return "#00E676";
    if (v >= 0.3) return "#4CAF50";
    if (v >= -0.3) return "#52525B";
    if (v >= -0.7) return "#FF7043";
    return "#FF3D00";
  };
  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full">
        <div className="grid gap-0.5" style={{ gridTemplateColumns: `72px repeat(${symbols.length}, minmax(48px, 1fr))` }}>
          <div />
          {symbols.map((s) => (
            <div key={s} className="text-[9px] text-[#A1A1AA] text-center truncate px-1">{s}</div>
          ))}
          {symbols.map((rowSym, ri) => (
            <Fragment key={rowSym}>
              <div className="text-[9px] text-[#A1A1AA] truncate pr-1 flex items-center">{rowSym}</div>
              {matrix[ri]?.map((val, ci) => (
                <div
                  key={`${rowSym}-${symbols[ci]}`}
                  className="aspect-square rounded flex items-center justify-center text-[9px] font-data font-bold text-white/90"
                  style={{ backgroundColor: cellColor(val), opacity: ri === ci ? 0.5 : 1 }}
                  title={`${rowSym} × ${symbols[ci]}: ${val}`}
                >
                  {ri === ci ? "1" : val.toFixed(2)}
                </div>
              ))}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ report, m, pieData, kpis }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
        {kpis.map((k) => (
          <div key={k.label} className="bg-[#111111] border border-[#27272A] rounded-xl p-3">
            <p className="text-[10px] text-[#52525B] uppercase tracking-wider mb-1">{k.label}</p>
            <p className="text-base sm:text-lg font-bold font-data" style={{ color: k.color }}>{k.value}</p>
          </div>
        ))}
      </div>

      <Card className="bg-[#111111] border-[#27272A]">
        <CardHeader className="pb-2 px-4 pt-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#00E676]" /> Equity Curve & Drawdown
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="h-64 sm:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={report.equity_curve} margin={{ top: 8, right: 12, left: 4, bottom: 32 }}>
                <defs>
                  <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.win} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={COLORS.win} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                <XAxis
                  dataKey="trade"
                  stroke="#52525B"
                  tick={{ fontSize: 10 }}
                  tickMargin={8}
                  height={40}
                  label={{ value: "Trade #", fontSize: 10, fill: "#52525B", position: "insideBottom", offset: -4 }}
                />
                <YAxis stroke="#52525B" tick={{ fontSize: 10 }} width={56} tickMargin={6} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="cumulative" stroke={COLORS.win} fill="url(#eqGrad)" strokeWidth={2} name="Cumulative P/L" />
                <Line type="monotone" dataKey="drawdown" stroke={COLORS.loss} strokeWidth={1.5} dot={false} name="Drawdown" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader className="pb-0 px-4 pt-4"><CardTitle className="text-sm">Monthly Returns</CardTitle></CardHeader>
          <CardContent className="p-4">
            <div className="h-56 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={report.monthly_returns}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                  <XAxis dataKey="month" stroke="#52525B" tick={{ fontSize: 9 }} angle={-35} textAnchor="end" height={50} />
                  <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="profit" radius={[4, 4, 0, 0]}>
                    {report.monthly_returns.map((e, i) => (
                      <Cell key={i} fill={e.profit >= 0 ? COLORS.win : COLORS.loss} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader className="pb-0 px-4 pt-4"><CardTitle className="text-sm">Win / Loss Split</CardTitle></CardHeader>
          <CardContent className="p-4">
            <div className="h-56 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: "#111111", border: "1px solid #27272A", borderRadius: 8, fontSize: 12 }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader className="pb-0 px-4 pt-4"><CardTitle className="text-sm">P/L by Symbol</CardTitle></CardHeader>
          <CardContent className="p-4">
            <div className="h-56 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={report.symbol_pnl} layout="vertical" margin={{ left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" horizontal={false} />
                  <XAxis type="number" stroke="#52525B" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="symbol" stroke="#52525B" tick={{ fontSize: 10 }} width={60} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="profit" radius={[0, 4, 4, 0]}>
                    {report.symbol_pnl.map((e, i) => (
                      <Cell key={i} fill={e.profit >= 0 ? COLORS.win : COLORS.loss} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader className="pb-0 px-4 pt-4"><CardTitle className="text-sm">P/L by Day of Week</CardTitle></CardHeader>
          <CardContent className="p-4">
            <div className="h-56 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={report.daily_pnl}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                  <XAxis dataKey="day" stroke="#52525B" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="profit" radius={[4, 4, 0, 0]}>
                    {report.daily_pnl.map((e, i) => (
                      <Cell key={i} fill={e.profit >= 0 ? COLORS.win : COLORS.loss} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {report.recommendations?.length > 0 && (
        <Card className="bg-[#111111] border-[#007AFF]/20">
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm text-[#007AFF]">Insights & Recommendations</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ul className="space-y-2">
              {report.recommendations.map((rec, i) => (
                <li key={i} className="text-xs text-[#A1A1AA] flex gap-2">
                  <span className="text-[#007AFF] shrink-0">•</span>{rec}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DistributionTab({ dist }) {
  const chartData = useMemo(() => mergeDistributionData(dist), [dist]);
  const cdfData = useMemo(() => {
    if (!dist?.cdf_empirical?.length) return [];
    const emp = dist.cdf_empirical || [];
    const norm = dist.cdf_normal || [];
    const xs = [...new Set([...emp.map((p) => p.x), ...norm.map((p) => p.x)])].sort((a, b) => a - b);
    const near = (arr, x) => {
      let best = arr[0];
      if (!best) return 0;
      for (const p of arr) {
        if (Math.abs(p.x - x) < Math.abs(best.x - x)) best = p;
      }
      return best.y;
    };
    return xs.map((x) => ({ x, empirical: near(emp, x), normal: near(norm, x) }));
  }, [dist]);

  if (!dist?.histogram?.length) return <EmptySection />;

  const qqData = dist.qq_plot || [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: "Mean", value: dist.mean },
          { label: "Std Dev", value: dist.std },
          { label: "Skewness", value: dist.skewness },
          { label: "Kurtosis", value: dist.kurtosis },
        ].map((s) => (
          <div key={s.label} className="bg-[#111111] border border-[#27272A] rounded-xl p-3">
            <p className="text-[10px] text-[#52525B] uppercase">{s.label}</p>
            <p className="font-data font-bold text-white">{s.value}</p>
          </div>
        ))}
      </div>

      <Card className="bg-[#111111] border-[#27272A]">
        <CardHeader className="pb-0 px-4 pt-4">
          <CardTitle className="text-sm">P/L Distribution — Histogram + Gaussian + KDE</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="h-64 sm:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                <XAxis dataKey="mid" stroke="#52525B" tick={{ fontSize: 10 }} label={{ value: "P/L ($)", fontSize: 10, fill: "#52525B" }} />
                <YAxis stroke="#52525B" tick={{ fontSize: 10 }} label={{ value: "Frequency", fontSize: 10, fill: "#52525B", angle: -90, dx: -10 }} />
                <Tooltip contentStyle={{ backgroundColor: "#111111", border: "1px solid #27272A", borderRadius: 8, fontSize: 11 }} />
                <Legend />
                <ReferenceLine x={0} stroke="#52525B" strokeDasharray="4 4" />
                <Bar dataKey="count" name="Frequency" radius={[2, 2, 0, 0]}>
                  {chartData.map((e, i) => (
                    <Cell key={i} fill={e.side === "win" ? COLORS.win : COLORS.loss} fillOpacity={0.85} />
                  ))}
                </Bar>
                <Line type="monotone" dataKey="gaussian" name="Gaussian fit" stroke={COLORS.gauss} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="kde" name="KDE" stroke={COLORS.kde} strokeWidth={2} dot={false} strokeDasharray="4 2" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader className="pb-0 px-4 pt-4">
            <CardTitle className="text-sm flex items-center justify-between">
              <span>Q-Q Plot</span>
              <span className={`text-[10px] font-normal ${dist.is_normal ? "text-[#00E676]" : "text-[#FFC107]"}`}>
                Shapiro-Wilk p = {dist.shapiro_p ?? "N/A"} · {dist.is_normal ? "Normal" : "Non-normal"}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                  <XAxis type="number" dataKey="theoretical" stroke="#52525B" tick={{ fontSize: 10 }} name="Theoretical" label={{ value: "Theoretical Quantiles", fontSize: 10, fill: "#52525B" }} />
                  <YAxis type="number" dataKey="sample" stroke="#52525B" tick={{ fontSize: 10 }} label={{ value: "Sample Quantiles", fontSize: 10, fill: "#52525B", angle: -90, dx: -10 }} />
                  <Tooltip contentStyle={{ backgroundColor: "#111111", border: "1px solid #27272A", borderRadius: 8, fontSize: 11 }} />
                  <Scatter data={qqData} fill={COLORS.neutral} name="Observed" />
                  {(dist.qq_line?.length === 2) && (
                    <Line data={dist.qq_line.map((p) => ({ theoretical: p.x, sample: p.y }))} dataKey="sample" stroke={COLORS.loss} strokeWidth={2} dot={false} name="Normal line" />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader className="pb-0 px-4 pt-4"><CardTitle className="text-sm">Empirical vs Normal CDF</CardTitle></CardHeader>
          <CardContent className="p-4">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cdfData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                  <XAxis dataKey="x" stroke="#52525B" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 1]} stroke="#52525B" tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ backgroundColor: "#111111", border: "1px solid #27272A", borderRadius: 8, fontSize: 11 }} />
                  <Legend />
                  <Line type="monotone" dataKey="empirical" name="Empirical" stroke={COLORS.neutral} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="normal" name="Normal" stroke={COLORS.amber} strokeWidth={2} dot={false} strokeDasharray="5 3" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader className="pb-0 px-4 pt-4"><CardTitle className="text-sm">Win vs Loss Magnitude</CardTitle></CardHeader>
          <CardContent className="p-4">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                  <XAxis dataKey="mid" stroke="#52525B" tick={{ fontSize: 9 }} />
                  <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ backgroundColor: "#111111", border: "1px solid #27272A", borderRadius: 8, fontSize: 11 }} />
                  <Legend />
                  <Bar data={dist.win_histogram} dataKey="count" name="Wins ($)" fill={COLORS.win} radius={[2, 2, 0, 0]} />
                  <Bar data={dist.loss_histogram} dataKey="count" name="Losses ($ abs)" fill={COLORS.loss} radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader className="pb-0 px-4 pt-4">
            <CardTitle className="text-sm">R-Multiple Distribution (avg {dist.avg_r_multiple}R)</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dist.r_multiple_histogram}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                  <XAxis dataKey="mid" stroke="#52525B" tick={{ fontSize: 9 }} label={{ value: "R-multiple", fontSize: 10, fill: "#52525B" }} />
                  <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ backgroundColor: "#111111", border: "1px solid #27272A", borderRadius: 8, fontSize: 11 }} />
                  <ReferenceLine x={0} stroke="#52525B" />
                  <ReferenceLine x={1} stroke={COLORS.win} strokeDasharray="4 4" label={{ value: "1R", fill: COLORS.win, fontSize: 10 }} />
                  <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                    {(dist.r_multiple_histogram || []).map((e, i) => (
                      <Cell key={i} fill={e.mid >= 0 ? COLORS.win : COLORS.loss} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function AdvancedTab({ advanced }) {
  if (!advanced?.scorecard?.length) return <EmptySection />;

  const winScatter = (advanced.mae_mfe || []).filter((p) => p.win);
  const lossScatter = (advanced.mae_mfe || []).filter((p) => !p.win);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {advanced.scorecard.map((s) => (
          <div key={s.name} className="bg-[#111111] border border-[#27272A] rounded-xl p-3">
            <p className="text-[10px] text-[#52525B] uppercase">{s.name}</p>
            <p className="text-lg font-bold font-data" style={{ color: STATUS_COLORS[s.status] || COLORS.neutral }}>{s.value}</p>
            <p className="text-[9px] text-[#52525B] mt-0.5">{s.note}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader className="pb-0 px-4 pt-4">
            <CardTitle className="text-sm">Rolling Sharpe (window {advanced.rolling_window})</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={advanced.rolling_sharpe}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                  <XAxis dataKey="trade" stroke="#52525B" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
                  <Tooltip content={<ChartTooltip prefix="" suffix="" />} />
                  <ReferenceLine y={1.5} stroke={COLORS.win} strokeDasharray="4 4" label={{ value: "1.5", fill: COLORS.win, fontSize: 10 }} />
                  <ReferenceLine y={0.5} stroke={COLORS.loss} strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="sharpe" stroke={COLORS.neutral} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader className="pb-0 px-4 pt-4"><CardTitle className="text-sm">Drawdown Depth</CardTitle></CardHeader>
          <CardContent className="p-4">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={advanced.drawdown_depth}>
                  <defs>
                    <linearGradient id="ddGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS.loss} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={COLORS.loss} stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                  <XAxis dataKey="trade" stroke="#52525B" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="drawdown" stroke={COLORS.loss} fill="url(#ddGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader className="pb-0 px-4 pt-4"><CardTitle className="text-sm">MAE vs MFE (pips)</CardTitle></CardHeader>
          <CardContent className="p-4">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                  <XAxis type="number" dataKey="mae" name="MAE" stroke="#52525B" tick={{ fontSize: 10 }} label={{ value: "MAE (pips)", fontSize: 10, fill: "#52525B" }} />
                  <YAxis type="number" dataKey="mfe" name="MFE" stroke="#52525B" tick={{ fontSize: 10 }} label={{ value: "MFE (pips)", fontSize: 10, fill: "#52525B", angle: -90, dx: -10 }} />
                  <ZAxis range={[40, 40]} />
                  <Tooltip cursor={{ strokeDasharray: "3 3" }} contentStyle={{ backgroundColor: "#111111", border: "1px solid #27272A", borderRadius: 8, fontSize: 11 }} />
                  <Legend />
                  <Scatter name="Wins" data={winScatter} fill={COLORS.win} />
                  <Scatter name="Losses" data={lossScatter} fill={COLORS.loss} />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader className="pb-0 px-4 pt-4"><CardTitle className="text-sm">Win/Loss Streak Waterfall</CardTitle></CardHeader>
          <CardContent className="p-4">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={advanced.streaks}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                  <XAxis dataKey="trade" stroke="#52525B" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ backgroundColor: "#111111", border: "1px solid #27272A", borderRadius: 8, fontSize: 11 }} />
                  <ReferenceLine y={0} stroke="#52525B" />
                  <Bar dataKey="streak" radius={[2, 2, 0, 0]}>
                    {(advanced.streaks || []).map((e, i) => (
                      <Cell key={i} fill={e.streak >= 0 ? COLORS.win : COLORS.loss} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111111] border-[#27272A] lg:col-span-2">
          <CardHeader className="pb-0 px-4 pt-4">
            <CardTitle className="text-sm">
              VaR / CVaR Tail Distribution — VaR₉₅ ${advanced.var_95} · CVaR₉₅ ${advanced.cvar_95}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={advanced.var_histogram}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                  <XAxis dataKey="mid" stroke="#52525B" tick={{ fontSize: 9 }} />
                  <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ backgroundColor: "#111111", border: "1px solid #27272A", borderRadius: 8, fontSize: 11 }} />
                  <ReferenceLine x={advanced.var_95} stroke={COLORS.loss} strokeWidth={2} label={{ value: "VaR₉₅", fill: COLORS.loss, fontSize: 10 }} />
                  <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                    {(advanced.var_histogram || []).map((e, i) => (
                      <Cell key={i} fill={e.tail ? COLORS.loss : COLORS.neutral} fillOpacity={e.tail ? 0.9 : 0.5} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function RegimeStyleTab({ report, regimeStyle }) {
  const rd = regimeStyle || {};
  const regimeDual = rd.regime_dual || [];
  const stylePie = rd.style_pie || [];
  const stylePnl = rd.style_pnl || [];
  const correlation = rd.correlation || { symbols: [], matrix: [] };
  const hourlyPnl = report?.hourly_pnl || [];
  const dailyPnl = report?.daily_pnl || [];

  const hasRegime = regimeDual.length > 0;
  const hasStyle = stylePie.length > 0 || stylePnl.length > 0;
  const hasTiming = hourlyPnl.length > 0 || dailyPnl.length > 0;
  const hasCorrelation = (correlation.symbols || []).length >= 2;

  if (!hasRegime && !hasStyle && !hasTiming) {
    return <EmptySection message="Re-sync your MT5 journal to generate regime & style analysis." />;
  }

  const pieColors = [COLORS.neutral, COLORS.purple, COLORS.amber, COLORS.win, COLORS.loss];
  const pieChartData = stylePie.map((s) => ({ name: s.style, count: s.count }));
  const stylePnlChart = stylePnl.map((s) => ({ name: s.style, profit: s.profit }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {hasRegime && (
          <>
            <Card className="bg-[#111111] border-[#27272A]">
              <CardHeader className="pb-0 px-4 pt-4"><CardTitle className="text-sm">Win Rate & Profit Factor by Regime</CardTitle></CardHeader>
              <CardContent className="p-4">
                <ChartBox>
                  <ComposedChart data={regimeDual}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                    <XAxis dataKey="regime" stroke="#52525B" tick={{ fontSize: 9 }} />
                    <YAxis yAxisId="left" stroke={COLORS.neutral} tick={{ fontSize: 10 }} unit="%" />
                    <YAxis yAxisId="right" orientation="right" stroke={COLORS.amber} tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ backgroundColor: "#111111", border: "1px solid #27272A", borderRadius: 8, fontSize: 11 }} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="win_rate" name="Win Rate %" fill={COLORS.neutral} radius={[3, 3, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="profit_factor" name="Profit Factor" stroke={COLORS.amber} strokeWidth={2} dot />
                  </ComposedChart>
                </ChartBox>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-[#27272A]">
              <CardHeader className="pb-0 px-4 pt-4"><CardTitle className="text-sm">Net P/L by Regime</CardTitle></CardHeader>
              <CardContent className="p-4">
                <ChartBox>
                  <BarChart data={regimeDual}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                    <XAxis dataKey="regime" stroke="#52525B" tick={{ fontSize: 9 }} />
                    <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="total_profit" radius={[4, 4, 0, 0]}>
                      {regimeDual.map((e, i) => (
                        <Cell key={i} fill={e.total_profit >= 0 ? COLORS.win : COLORS.loss} />
                      ))}
                    </Bar>
                  </BarChart>
                </ChartBox>
              </CardContent>
            </Card>
          </>
        )}

        {hasTiming && hourlyPnl.length > 0 && (
          <Card className="bg-[#111111] border-[#27272A]">
            <CardHeader className="pb-0 px-4 pt-4"><CardTitle className="text-sm">P/L by Hour (UTC)</CardTitle></CardHeader>
            <CardContent className="p-4">
              <ChartBox className="h-48">
                <BarChart data={hourlyPnl}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                  <XAxis dataKey="hour" stroke="#52525B" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="profit" radius={[3, 3, 0, 0]}>
                    {hourlyPnl.map((e, i) => (
                      <Cell key={i} fill={e.profit >= 0 ? COLORS.win : COLORS.loss} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartBox>
            </CardContent>
          </Card>
        )}

        {hasTiming && dailyPnl.length > 0 && (
          <Card className="bg-[#111111] border-[#27272A]">
            <CardHeader className="pb-0 px-4 pt-4"><CardTitle className="text-sm">P/L by Day of Week</CardTitle></CardHeader>
            <CardContent className="p-4">
              <ChartBox className="h-48">
                <BarChart data={dailyPnl}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" />
                  <XAxis dataKey="day" stroke="#52525B" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="profit" radius={[4, 4, 0, 0]}>
                    {dailyPnl.map((e, i) => (
                      <Cell key={i} fill={e.profit >= 0 ? COLORS.win : COLORS.loss} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartBox>
            </CardContent>
          </Card>
        )}

        {hasStyle && stylePie.length > 0 && (
          <Card className="bg-[#111111] border-[#27272A]">
            <CardHeader className="pb-0 px-4 pt-4"><CardTitle className="text-sm">Trade Style Mix</CardTitle></CardHeader>
            <CardContent className="p-4">
              <ChartBox className="h-48">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    label={({ name, count }) => `${name}: ${count}`}
                  >
                    {pieChartData.map((_, i) => (
                      <Cell key={i} fill={pieColors[i % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: "#111111", border: "1px solid #27272A", borderRadius: 8, fontSize: 11 }} />
                </PieChart>
              </ChartBox>
            </CardContent>
          </Card>
        )}

        {hasStyle && stylePnl.length > 0 && (
          <Card className="bg-[#111111] border-[#27272A]">
            <CardHeader className="pb-0 px-4 pt-4"><CardTitle className="text-sm">Net P/L by Style</CardTitle></CardHeader>
            <CardContent className="p-4">
              <ChartBox className="h-48">
                <BarChart data={stylePnlChart} layout="vertical" margin={{ left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1C1C1E" horizontal={false} />
                  <XAxis type="number" stroke="#52525B" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" stroke="#52525B" tick={{ fontSize: 10 }} width={56} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="profit" radius={[0, 4, 4, 0]}>
                    {stylePnlChart.map((e, i) => (
                      <Cell key={i} fill={e.profit >= 0 ? COLORS.win : COLORS.loss} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartBox>
            </CardContent>
          </Card>
        )}

        {hasCorrelation && (
          <Card className="bg-[#111111] border-[#27272A] lg:col-span-2">
            <CardHeader className="pb-0 px-4 pt-4"><CardTitle className="text-sm">Instrument Correlation Heatmap</CardTitle></CardHeader>
            <CardContent className="p-4">
              <CorrelationHeatmap correlation={correlation} />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default function PerformanceReports({ report, loading }) {
  const [activeTab, setActiveTab] = useState("overview");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-[#52525B]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Building performance report...
      </div>
    );
  }

  if (report?.pending) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-[#52525B] gap-2">
        <Loader2 className="w-6 h-6 animate-spin text-[#007AFF]" />
        <p className="text-sm">{report.message || "Syncing journal and compiling charts on executor…"}</p>
      </div>
    );
  }

  if (!report?.ok) {
    return (
      <div className="text-center py-16 px-4">
        <AlertCircle className="w-10 h-10 text-[#52525B] mx-auto mb-3" />
        <p className="text-sm text-[#52525B]">{report?.message || "Link MT5 and sync your trade journal to see performance charts."}</p>
      </div>
    );
  }

  const m = report.metrics;
  const pieData = [
    { name: "Wins", value: report.win_loss_split.wins, color: COLORS.win },
    { name: "Losses", value: report.win_loss_split.losses, color: COLORS.loss },
    ...(report.win_loss_split.breakeven ? [{ name: "BE", value: report.win_loss_split.breakeven, color: COLORS.amber }] : []),
  ];

  const kpis = [
    { label: "Net Profit", value: `$${m.net_profit.toLocaleString()}`, color: m.net_profit >= 0 ? COLORS.win : COLORS.loss },
    { label: "Win Rate", value: `${m.win_rate}%`, color: COLORS.neutral },
    { label: "Profit Factor", value: m.profit_factor, color: m.profit_factor >= 1.5 ? COLORS.win : COLORS.amber },
    { label: "Max Drawdown", value: `$${m.max_drawdown.toLocaleString()}`, color: COLORS.loss },
    { label: "Sharpe", value: m.sharpe, color: m.sharpe >= 1.5 ? COLORS.win : COLORS.amber },
    { label: "Expectancy", value: `$${m.expectancy}`, color: m.expectancy >= 0 ? COLORS.win : COLORS.loss },
  ];

  const hasExtended = Boolean(report.distribution?.histogram?.length);

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
      <TabsList className="bg-[#111111] border border-[#27272A] h-auto flex-wrap gap-1 p-1 w-full justify-start">
        <TabsTrigger value="overview" className="text-xs data-[state=active]:bg-[#007AFF] data-[state=active]:text-white">
          <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Overview
        </TabsTrigger>
        <TabsTrigger value="distribution" className="text-xs data-[state=active]:bg-[#007AFF] data-[state=active]:text-white">
          <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Distribution
        </TabsTrigger>
        <TabsTrigger value="advanced" className="text-xs data-[state=active]:bg-[#007AFF] data-[state=active]:text-white">
          <Activity className="w-3.5 h-3.5 mr-1.5" /> Advanced
        </TabsTrigger>
        <TabsTrigger value="regime" className="text-xs data-[state=active]:bg-[#007AFF] data-[state=active]:text-white">
          <Globe className="w-3.5 h-3.5 mr-1.5" /> Regime & Style
        </TabsTrigger>
      </TabsList>

      {!hasExtended && (
        <p className="text-[10px] text-[#FFC107] px-1">
          Extended charts loading — refresh the Charts tab or re-open this page after sync completes.
        </p>
      )}

      <TabsContent value="overview" forceMount={activeTab === "overview" ? undefined : false}>
        {activeTab === "overview" && <OverviewTab report={report} m={m} pieData={pieData} kpis={kpis} />}
      </TabsContent>
      <TabsContent value="distribution" forceMount={activeTab === "distribution" ? undefined : false}>
        {activeTab === "distribution" && <DistributionTab dist={report.distribution} />}
      </TabsContent>
      <TabsContent value="advanced" forceMount={activeTab === "advanced" ? undefined : false}>
        {activeTab === "advanced" && <AdvancedTab advanced={report.advanced} />}
      </TabsContent>
      <TabsContent value="regime" forceMount={activeTab === "regime" ? undefined : false}>
        {activeTab === "regime" && <RegimeStyleTab report={report} regimeStyle={report.regime_style} />}
      </TabsContent>
    </Tabs>
  );
}
